import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {makeStyles, useTheme} from "@material-ui/core/styles";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import storage, {StorageKey as SK} from "../../common/storage";
import {useTranslation} from "react-i18next";
import {useStores} from "../../common/store";
import api from "../../common/api";
import {AuthStatus, ErrCode} from "../../common/types";
import {stopQrLogin} from "./QrPage";

const useStyles = makeStyles((theme) => ({
    downLinkBox: {
        display: 'flex',
        [theme.breakpoints.between('sm', 'md')]: {
            marginTop: "20px",
            alignItems: "flex-end"
        },
        [theme.breakpoints.down("xs")]: {
            marginTop: "20px",
            alignItems: "flex-end"
        }
    },
    downLink: {
        display: 'flex',
        alignItems: 'center',
        padding: '3px 9px 0px 0px',
        boxSizing: 'border-box',
        lineHeight: '1.75',
        borderRadius: '4px',
        textDecoration: "none",
        '&:hover': {
            border: "none",
        },
        '& > img': {
            imageRendering: '-webkit-optimize-contrast'
        },
        [theme.breakpoints.down("xs")]: {
            padding: "0px"
        }
    },
    copyright: {
        color: "#9F9FA1",
        ...theme.typography.subtitle2,
        [theme.breakpoints.between('sm', 'md')]: {
            fontSize: "0.75rem",
        },
        [theme.breakpoints.down("xs")]: {
            fontSize: "0.5rem",
        }
    },
}));


function AppDownLink({clazz}) {
    const classes = useStyles();
    const theme = useTheme();

    const storeURL = useRef({aos: '', ios: ''});

    let androidIcon = "android_dark.png";
    let iosIcon = "ios_dark.png";

    if (theme.type === 'gccs') {
        if (theme.storeType === 'vip') {
            storeURL.current.aos = 'https://play.google.com/store/apps/details?id=com.fnsvalue.guardian.authenticator';
            storeURL.current.ios = 'https://apps.apple.com/us/app/guardianccs/id1469192314';
        } else {
            storeURL.current.aos = 'https://play.google.com/store/apps/details?id=com.fnsvalue.guardian.authenticator';
            storeURL.current.ios = 'https://apps.apple.com/us/app/guardianccs/id1469192314';
        }
    } else if (theme.type === 'bsa') {
        if( theme.storeType === 'azure') {
            storeURL.current.aos = 'https://play.google.com/store/apps/details?id=com.tm.bsa.azure';
            storeURL.current.ios = 'https://apps.apple.com/us/app/bsa-azure/id1568992310';
        } else if( theme.storeType === 'poc') {
            storeURL.current.aos = 'https://play.google.com/store/apps/details?id=com.tm.bsa';
            storeURL.current.ios = 'https://apps.apple.com/us/app/bsa-tmpoc/id1563337320';
        } else if (theme.storeType === 'prod') {
            storeURL.current.aos = 'https://play.google.com/store/apps/details?id=com.tm.bsa.clients';
            storeURL.current.ios = 'https://apps.apple.com/us/app/bsa-auth/id1581513812';
        }
    }

    const androidAppDownLoad = () => {
        return (
            <Link key={'androidAppDownLoad'} href={storeURL.current.aos} target={'_blank'} className={classes.downLink}>
                <img alt={'icon'} src={require('../../images/' + androidIcon)} width={80}/>
            </Link>
        );
    }

    const iosAppDownLoad = () => {
        return (
            <Link key={'iosAppDownLoad'} href={storeURL.current.ios} target={'_blank'} className={classes.downLink}>
                <img alt={'icon'} src={require('../../images/' + iosIcon)} width={80}/>
            </Link>
        );
    }

    const deviceAppDownLoad = () => {
        const device = navigator.userAgent;
        if (device.match(".*Android.*")) {
            // 안드로이드 기기
            return androidAppDownLoad();
        } else if (device.match(".*iPhone.*") || device.match(".*iPad.*")) {
            // ios 기기
            return iosAppDownLoad();
        }
        // 안드로이드, ios 제외 모든 기기
        // return [androidAppDownLoad(), iosAppDownLoad()];
        return [];
    }

    const deviceAppLoads = deviceAppDownLoad();

    return (
        <Box size='small' className={`${ clazz ? clazz : classes.downLinkBox}`}>
            {deviceAppLoads}
        </Box>
    );
}


function ProgressAlert({status}) {
    return (
        <Alert severity="info">{status}</Alert>
    );
}


function StatusAlert({status, onClose}) {
    return (
        <Alert severity="error" onClose={onClose}>{status}</Alert>
    );
}

function timeout(delay) {
    return new Promise(res => setTimeout(res, delay));
}

/** 로그인 인증 함수 (모바일 인증, QR, OTP, TOTP) */
const LoginFunc = forwardRef( (props, ref) => {

    const {ds} = useStores();
    const {t} = useTranslation();
    const {parentRef} = props;
    let fncClearInterval = useRef();

    const stompConnector = React.useRef(null);
    const isFinished = React.useRef(false);


    const userKeyHandler = ( value ) => {
        parentRef.current?.setUserKey(value);
    }

    const onUserKeyChange = ( {target} ) => {
        userKeyHandler( target.value );
    }

    const onLogin = () => {

        // token login
        // api.setToken('ewogICJhbGciOiAiSFMzODQiCn0.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.7U_Kac-nzlFi4VXtjLfSGfQw-G3o8kWtPGCbmYCEi8k4QtPxLsG7Qv-mIY5FFizS');
        // api.getMe()
        //     .then(data => {
        //         ds.setLogin(true);
        //         storage.setSession(SK.LOGIN_USER_INFO, {data});
        //     })
        //     .catch(err => {
        //         parentRef.current?.setErrMsg(err.rtMsg);
        //     });
        // return;


        parentRef.current?.setErrMsg(null);
        const userKey = parentRef.current?.userKey;

        if (userKey === null || userKey.length <= 0) {
            parentRef.current?.setErrMsg(t('LoginPage.LoginNoID'));
            return;
        }

        if (parentRef.current?.rememberMe) {
            // 로그인정보를 저장한다.
            const masterClientKey = parentRef.current?.masterClientKey;
            const rememberMe = parentRef.current?.rememberMe;

            storage.setLocal(SK.LOGIN_INFO, { masterClientKey, userKey, rememberMe});
        } else {
            // 로그인정보를 삭제한다.
            storage.removeLocal(SK.LOGIN_INFO);
        }
        onLoginProcess(userKey);

    }

    const onLoginProcess = (p_userKey, isOtpAuth = false) => {

        isFinished.current = false;

        parentRef.current?.setProgressMsg(t('AuthStatus.RequestAuth'));
        const masterClientKey = parentRef.current?.masterClientKey;
        api.requestAuth(masterClientKey, p_userKey, isOtpAuth)
            .then(res => {
                if (res.rtCode === ErrCode.RT_SUCCESS) {
                    const authTimeRemaining = res.data.authTimeRemaining;
                    const channelKey = res.data.channelKey;

                    fncClearInterval.current = customInterval((authTimeRemaining / 1000) + 1, null, t('LoginPage.LoginTimeout'));

                    parentRef.current?.setProgressMsg(t('AuthStatus.CreateChannel'));
                    parentRef.current?.setProgressMsg(t('AuthStatus.SelectNodes'));
                    parentRef.current?.setProgressMsg(t('AuthStatus.CheckDevice'));

                    stompConnector.current = api.getStompWS(masterClientKey, p_userKey, channelKey)

                    stompConnector.current.onConnect = ( data ) => {

                        const subscribeCallBack = ( message ) => {
                            const data = JSON.parse(message.body);
                            const status = data.status;
                            const statusMsg = t('AuthStatus.' + status);

                            if( AuthStatus.isFinish( status ) ) {
                                subscribeStomp.unsubscribe();
                                parentRef.current?.setProgressMsg(null);

                                isFinished.current = true;

                                if (AuthStatus.isSuccess(status)) {
                                    if (isOtpAuth) {
                                        parentRef.current?.setOtpOpen(true);
                                    } else { /* END if (isOtpAuth) { */
                                        api.getAuthResult(masterClientKey, p_userKey, channelKey)
                                            .then(data => {
                                                if (data.rtCode === ErrCode.RT_SUCCESS) {
                                                    api.setToken(data.data);
                                                    api.getMe()
                                                        .then(data => {
                                                            ds.setLogin(true);
                                                            storage.setSession(SK.LOGIN_USER_INFO, {data});
                                                        })
                                                        .catch(err => {
                                                            parentRef.current?.setErrMsg(err.rtMsg);
                                                        });
                                                } else {
                                                    parentRef.current?.setErrMsg(api.getErrMsg(data.rtCode));
                                                    parentRef.current?.setProgressMsg(null);
                                                }
                                            })
                                            .catch(err => {
                                                parentRef.current?.setErrMsg(err.rtMsg);
                                            });
                                    }
                                } else { /* END if (AuthStatus.isSuccess(status)) { */
                                    parentRef.current?.setErrMsg(statusMsg);
                                    parentRef.current?.setProgressMsg(null);
                                    if ( fncClearInterval.current !== null ) { fncClearInterval.current(); }
                                }
                            } else { /* END if( AuthStatus.isFinish( status ) ) {*/
                                parentRef.current?.setProgressMsg(statusMsg);
                            }
                        }

                        const subscribeStomp = stompConnector.current.subscribe('/user/queue', (message) => {

                            if( message.command === "MESSAGE" ) {
                                subscribeCallBack(message);
                            } else { /* END if( message.command === "MESSAGE" ) { */
                                parentRef.current?.setErrMsg(t('AuthStatus.AuthFailed'));
                                parentRef.current?.setProgressMsg(null);
                            }

                        });
                    };

                    stompConnector.current.onWebSocketClose = () => {
                        console.log(" Close Web Socket Event Test");

                        if( !isFinished.current ) {
                            api.getAuthResult(masterClientKey, p_userKey, channelKey)
                                .then(data => {
                                    if (data.rtCode === ErrCode.RT_SUCCESS) {
                                        api.setToken(data.data);
                                        api.getMe()
                                            .then(data => {
                                                ds.setLogin(true);
                                                storage.setSession(SK.LOGIN_USER_INFO, {data});
                                            })
                                            .catch(err => {
                                                parentRef.current?.setErrMsg(err.rtMsg);
                                            });
                                    }  else {
                                        if( data.rtCode !== 2010 ) {
                                            parentRef.current?.setErrMsg(api.getErrMsg(data.rtCode));
                                            parentRef.current?.setProgressMsg(null);
                                        }
                                    }
                                })
                                .catch(err => {
                                    parentRef.current?.setErrMsg(err.rtMsg);
                                });
                        }

                    };

                    stompConnector.current.onDisconnect = () => {
                        console.log("close connect event test");
                    }

                } else {
                    parentRef.current?.setErrMsg(api.getErrMsg(res.rtCode));
                }

            })
            .catch(err => {
                if (fncClearInterval.current != null) {
                    fncClearInterval.current();
                }
                parentRef.current?.setErrMsg(err.rtMsg);
                parentRef.current?.setProgressMsg(null);
            });



    }

    const customInterval = (time, onClose, timeoutMsg = null) => {
        let count = time;

        const id = setInterval(() => {
            count = count - 1;
            if (count) { parentRef.current?.setCountDown(count); }
            else { clear(true); }
        }, 1000);


        const clear = (isTimeout) => {
            if (id !== null) {
                if( stompConnector.current !== null ) {
                    console.log("stompConnector.current.deactivate();");
                    stompConnector.current.deactivate();
                }
                clearInterval(id);
                if (isTimeout && timeoutMsg) {
                    isFinished.current = true;
                    parentRef.current?.setErrMsg(timeoutMsg);
                }
                if (onClose) { onClose(); }
                parentRef.current?.setCountDown(null);
            }
        }

        return () => { clear(false) };
    }

    const onErrorHandler = () => {
        parentRef.current?.setErrMsg(null);
        parentRef.current?.setProgressMsg(null);
    }

    const onCancel = () => {
        isFinished.current = true;

        api.deleteAuth(parentRef.current?.masterClientKey, parentRef.current?.otpUserKey ? parentRef.current?.otpUserKey : parentRef.current?.userKey)
            .then(data => {
                if (data.rtCode === ErrCode.RT_SUCCESS) {
                    parentRef.current?.setProgressMsg(null);
                    if (fncClearInterval.current !== undefined) { fncClearInterval.current(); }
                }
            }).catch(err => {
                parentRef.current?.setErrMsg(err.rtMsg);
            });

    }

    const onRememberMe = (event) => {
        parentRef.current?.setRememberMe(event.target.checked);
    };

    const onQrLogin = () => {
        api.getLoginQrUrl(parentRef.current?.masterClientKey)
            .then((data) => {
                parentRef.current?.setQrData( {open:true, qrId:data.qrId, qrUrl:data.qrUrl});
            })
            .catch((err) => {
                parentRef.current?.setErrMsg(err.rtMsg);
            })
    }

    const onQrCancel = (isStop = false, userKey = null) => {
        parentRef.current?.setQrData({open:false, qrId:'', qrUrl: ''});
        if( isStop ) { stopQrLogin(parentRef.current?.qrData?.qrId, userKey, parentRef.current?.masterClientKey, parentRef.current?.setProgressMsg, parentRef.current?.setErrMsg); }
    }

    const onOTPLogin = () => {
        if (parentRef.current?.userKey) {
            onLoginProcess(parentRef.current?.userKey, true);
        } else {
            parentRef.current?.setOtpOpen(true);
        }
    }

    const onOTPSuccess = ({otpUserKey = null, token = null}) => {
        if (otpUserKey) {
            parentRef.current?.setErrMsg(null);
            if (otpUserKey.length <= 0) {
                parentRef.current?.setErrMsg(t('LoginPage.LoginNoID'));
                return;
            }

            parentRef.current?.setOtpUserKey(otpUserKey);
            onLoginProcess(otpUserKey);
        } else if (token) {
            if (parentRef.current?.rememberMe) {
                const _masterClientKey = parentRef.current?.masterClientKey;
                const _userKey = parentRef.current?.userKey;
                const _rememberMe = parentRef.current?.rememberMe;
                storage.setLocal(SK.LOGIN_INFO, {_masterClientKey, _userKey, _rememberMe});
            } else {
                storage.removeLocal(SK.LOGIN_INFO);
            }

            onOTPTokenSuccess({token});
        }
    }

    const onOTPTokenSuccess = async ({token}) => {
        api.setToken(token);
        await timeout(1000);
        api.getMe()
            .then(data => {
                ds.setLogin(true);
                storage.setSession(SK.LOGIN_USER_INFO, {data});
            })
            .catch(err => {
                parentRef.current?.setErrMsg(err.rtMsg);
            });
    }

    useImperativeHandle(ref , () => ({
        userKeyHandler : userKeyHandler,
        onUserKeyChange : onUserKeyChange,
        onLogin : onLogin,
        onCancel : onCancel,
        onErrorHandler : onErrorHandler,
        onRememberMe : onRememberMe,
        onQrLogin : onQrLogin,
        onQrCancel : onQrCancel,
        onOTPLogin : onOTPLogin,
        onOTPSuccess : onOTPSuccess,
        onOTPTokenSuccess : onOTPTokenSuccess,
    }));


});


export { AppDownLink, StatusAlert, ProgressAlert };
export default LoginFunc;
